import { Fragment } from 'react';
import { KeenIcon } from '@/components';
import { Container } from '@/components/container';
import { toAbsoluteUrl } from '@/utils';

import { OfferDetailsData } from '@/pages/hettwer/types/OfferDetailsData';
import { formatDateOnly } from '@/pages/buddy/utils/Date.tsx';
import { formatDateOnlyWithRelative } from '@/pages/hettwer/utils/Date.tsx';
import { getSensitiveFieldClassName, usePrivacyMode } from '@/utils/privacyMode';

interface OfferProfileHeroProps {
  offer: OfferDetailsData;
}

const OfferProfileHero = ({ offer }: OfferProfileHeroProps) => {
  const { isPrivacyMode } = usePrivacyMode();

  const isFinal = offer.status === 'final';
  const isTraining = offer.training?.is_training_data === true;

  const statusBadge = isFinal
    ? 'bg-green-100 text-green-800 border-green-300'
    : 'bg-yellow-100 text-yellow-800 border-yellow-300';

  const statusLabel = isFinal ? 'Fertiges Dokument' : 'Entwurf';

  const documentLabel = offer.document_type === 'rechnung' ? 'Rechnung' : 'Angebot';

  const summaryText = offer.summary?.subject || offer.summary?.project_type || null;

  return (
    <Fragment>
      <style>
        {`
          .hero-bg {
            background-image: url('${toAbsoluteUrl('/media/images/2600x1200/bg-1.png')}');
          }
          .dark .hero-bg {
            background-image: url('${toAbsoluteUrl('/media/images/2600x1200/bg-1-dark.png')}');
          }
        `}
      </style>

      <div className="bg-center bg-cover bg-no-repeat hero-bg">
        <Container>
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <div className="flex items-center justify-center rounded-full border-2 border-primary bg-light h-[90px] w-[90px]">
              <KeenIcon icon="document" className="text-primary text-3xl" />
            </div>

            <div className="text-2xl font-semibold text-gray-800">
              {documentLabel}
              {offer.offer_number ? ` ${offer.offer_number}` : ''}
            </div>

            {offer.customer?.name && (
              <div className="flex items-center gap-1 text-gray-700 text-sm mt-2">
                <KeenIcon icon="user" className="text-primary" />
                <span className={getSensitiveFieldClassName(isPrivacyMode)}>
                  {offer.customer.name}
                </span>
              </div>
            )}

            {summaryText && <div className="text-sm text-gray-600 max-w-2xl">{summaryText}</div>}

            <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusBadge}`}>
                {statusLabel}
              </span>

              {isTraining && (
                <span className="px-3 py-1 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-300">
                  Trainingsdokument
                </span>
              )}
            </div>

            {(offer.offer_date || offer.valid_until) && (
              <div className="text-xs text-gray-500 mt-1">
                {offer.offer_date && (
                  <>Erstellt am {formatDateOnlyWithRelative(offer.offer_date)}</>
                )}
                {offer.offer_date && offer.valid_until && ' · '}
                {offer.valid_until && <>gültig bis {formatDateOnly(offer.valid_until)}</>}
              </div>
            )}
          </div>
        </Container>
      </div>
    </Fragment>
  );
};

export { OfferProfileHero };
